/**
 * AP-46 21. — **Diktat mit KI** (Baustein `SITE_DOCS.AI`): Einwilligung, Einstellung, Vorschlag und die
 * Mengenprobe. Gerät, ERP und Server lesen dieselben Texte und dieselbe Probe.
 */

/** Fassung des Hinweises — eine Zustimmung ohne die geltende Fassung zählt wie keine (21.4). */
export const BAUSTELLE_KI_EINWILLIGUNG = 'baustelle-ki/1'

export const BAUSTELLE_KI_EINWILLIGUNG_TEXT =
  'Für das Auswerten von Diktaten geht der eingegebene Text — und nur dieser — an die KI und wird dort verarbeitet. Er kann Namen von Mitarbeitern und von Personen des Kunden enthalten. Fotos, Unterschriften und Stammdaten werden nicht übermittelt. Über den Zugang von Symphenia gilt dafür der KI-Zusatz (Einstellungen → Verträge); dort stehen der Dienst und der Ort der Verarbeitung. Mit einem eigenen KI-Zugang verarbeitet dein Anbieter die Daten nach seinen Bedingungen.'

export const BAUSTELLE_KI_EINWILLIGUNG_KAESTCHEN = 'Ich habe den Hinweis gelesen und schalte das Auswerten von Diktaten für den Betrieb ein.'

/** Höchstlänge des Diktattexts — gut fünf Minuten Diktat (21.5). */
export const SITE_DOC_DIKTAT_MAX = 4000

/**
 * AP-49 RN-8 — **Diktatlänge je Art.** Eine Art kann mehr zulassen (Besprechung: eine Mitschrift ist länger
 * als ein Regietag). Der Server prüft gegen `SiteDocKindDiktat.maxZeichen`; die Felder in PWA und ERP zeigen
 * die Grenze ihrer Art. Was hier fehlt, hat die Vorgabe.
 */
export const SITE_DOC_DIKTAT_MAX_JE_ART: Partial<Record<'REGIE' | 'ASSEMBLY' | 'DIARY' | 'MEETING', number>> = { MEETING: 12_000 }

/** Die größte Grenze aller Arten — Obergrenze der DTOs; die Art prüft genauer. */
export const SITE_DOC_DIKTAT_HOECHSTENS = Math.max(SITE_DOC_DIKTAT_MAX, ...Object.values(SITE_DOC_DIKTAT_MAX_JE_ART).map((n) => n ?? 0))

/** Grenze des Diktattexts einer Art (RN-8). */
export function siteDocDiktatMax(kind: string): number {
  return SITE_DOC_DIKTAT_MAX_JE_ART[kind as keyof typeof SITE_DOC_DIKTAT_MAX_JE_ART] ?? SITE_DOC_DIKTAT_MAX
}

/** `GET /site-docs/settings/ai` — der Schalter samt Hinweis (Text und Fassung kommen vom Server). */
export interface SiteDocAiSettings {
  /** gebucht, eingeschaltet und in der geltenden Fassung bestätigt */
  enabled: boolean
  acceptedAt: string | null
  acceptedByName: string | null
  /** Ist die KI des Mandanten eingerichtet (eigener Schlüssel oder Kontingent von Symphenia)? */
  aiAvailable: boolean
  consentText: string
  consentCheckbox: string
  consentVersion: string
}

/** `POST /site-docs/ai/proposal` */
export interface SiteDocAiProposalInput {
  kind: string
  text: string
  projectId?: string | null
  projectRefId?: string | null
  documentDate?: string | null
}

/**
 * Ein Eintrag des Vorschlags. Was ein Eintrag trägt, bestimmt die Art (`daten`); der Rahmen kennt nur, ob er
 * vorgewählt ist und was zu prüfen bleibt.
 */
export interface SiteDocAiProposalItem<T = Record<string, unknown>> {
  key: string
  daten: T
  /** Mengenprobe nicht bestanden — dann nicht vorgewählt. Offene Zuordnungen stehen in `hinweise`. */
  vorgewaehlt: boolean
  /** Hinweise am Eintrag: `NUMBER_NOT_IN_TEXT`, `PERSON_OPEN`, `ARTICLE_OPEN`, `UNIT_OPEN`, `SURCHARGE_OPEN`, `QUANTITY_OPEN` */
  hinweise: string[]
}

export interface SiteDocAiProposal<T = Record<string, unknown>> {
  kind: string
  items: SiteDocAiProposalItem<T>[]
  /** Angaben für den Kopf der Art (beim Regiebericht die Beschreibung), wenn die Art welche vorschlägt. */
  kopf?: Record<string, unknown>
  /** Hinweise der KI und der Zuordnung über den Einträgen („Bei Hans fehlt die Dauer"). */
  hinweise?: string[]
  model: string
}

/**
 * AP-27 18. — ein Eintrag aus dem Diktat beim Regiebericht: die Felder einer Regiezeile, schon zugeordnet
 * (Person, Artikel, Einheit, Zuschlag), soweit eindeutig. Übernommen wird mit `origin = DICTATION`.
 */
export interface SiteDocDiktatZeile {
  kind: 'SERVICE' | 'MATERIAL' | 'EQUIPMENT' | 'OTHER'
  description: string
  quantity: number
  unitCode: string
  unitId: string | null
  personName: string | null
  personUserId: string | null
  fromTime: string | null
  toTime: string | null
  articleId: string | null
  articleNumber: string | null
  articleName: string | null
  surchargeKey: string | null
  surchargeLabel: string | null
}

// ── Mengenprobe (BD-46) ─────────────────────────────────────────────────────

const EINER: Record<string, number> = {
  null: 0,
  ein: 1,
  eins: 1,
  eine: 1,
  einen: 1,
  einem: 1,
  einer: 1,
  zwei: 2,
  zwo: 2,
  drei: 3,
  vier: 4,
  fünf: 5,
  fuenf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  elf: 11,
  zwölf: 12,
  zwoelf: 12,
  dreizehn: 13,
  vierzehn: 14,
  fünfzehn: 15,
  sechzehn: 16,
  siebzehn: 17,
  achtzehn: 18,
  neunzehn: 19,
  zwanzig: 20,
  dreißig: 30,
  dreissig: 30,
  vierzig: 40,
  fünfzig: 50,
  sechzig: 60,
  siebzig: 70,
  achtzig: 80,
  neunzig: 90,
  hundert: 100,
}

const BRUCH: Record<string, number> = { halb: 0.5, halbe: 0.5, halben: 0.5, halber: 0.5, viertel: 0.25, dreiviertel: 0.75, anderthalb: 1.5 }

/** Ein Zahlwort lesen: „drei", „zweieinhalb", „einundzwanzig", „dreiviertel" — sonst `null`. */
function zahlwort(w: string): number | null {
  if (w in EINER) return EINER[w]
  if (w in BRUCH) return BRUCH[w]
  const einhalb = w.match(/^(.+?)einhalb$/)
  if (einhalb) {
    const g = zahlwort(einhalb[1])
    return g === null ? null : g + 0.5
  }
  const und = w.match(/^(.+?)und(.+)$/)
  if (und) {
    const e = zahlwort(und[1])
    const z = zahlwort(und[2])
    if (e !== null && z !== null && e < 10 && z % 10 === 0) return z + e
  }
  return null
}

/**
 * Alle Zahlen, die im Text vorkommen — als Ziffer („2", „2,5", „40×5", „9:30") oder als Zahlwort („drei",
 * „zweieinhalb", „halb" in „halbe Stunde"). Uhrzeiten zählen auch als Stunde und Minute einzeln.
 */
export function siteDocZahlenImText(text: string): number[] {
  const aus = new Set<number>()
  const t = text.toLowerCase()
  for (const m of t.matchAll(/\d+(?:[.,]\d+)?/g)) {
    aus.add(Number(m[0].replace(',', '.')))
    for (const teil of m[0].split(/[.,]/)) aus.add(Number(teil))
  }
  for (const wort of t.split(/[^a-zäöüß]+/).filter(Boolean)) {
    const z = zahlwort(wort)
    if (z !== null) aus.add(z)
  }
  // „zwei und eine halbe Stunde", „eine halbe Stunde" → auch die Summe
  for (const m of t.matchAll(/([a-zäöüß]+|\d+)\s+und\s+(?:eine\s+)?halbe?/g)) {
    const g = /^\d+$/.test(m[1]) ? Number(m[1]) : zahlwort(m[1])
    if (g !== null) aus.add(g + 0.5)
  }
  return [...aus]
}

/** Kommt die Zahl im Text vor? Toleranz für Rundung (2,5 ↔ 2.50). */
export function siteDocZahlImText(text: string, wert: number, zahlen = siteDocZahlenImText(text)): boolean {
  return zahlen.some((z) => Math.abs(z - wert) < 0.001)
}

/**
 * Kommt die Uhrzeit („HH:mm") im Text vor? Als Ziffer („9:30", „9.30", „09:30", „9 Uhr 30", „9 Uhr") oder
 * gesprochen („halb zehn", „viertel nach neun", „dreiviertel zehn", „neun Uhr").
 */
export function siteDocZeitImText(text: string, zeit: string): boolean {
  const m = zeit.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return false
  const h = Number(m[1])
  const min = Number(m[2])
  const t = text.toLowerCase()
  for (const z of t.matchAll(/(\d{1,2})\s*(?:[:.]\s*(\d{2})|uhr\s*(\d{1,2})?)/g)) {
    const zh = Number(z[1])
    const zm = Number(z[2] ?? z[3] ?? 0)
    if (zm === min && (zh === h || zh + 12 === h || zh === h + 12)) return true
  }
  const stunde = (w: string) => {
    const z = /^\d+$/.test(w) ? Number(w) : zahlwort(w)
    return z !== null && z >= 1 && z <= 24 ? z : null
  }
  const gleich = (zh: number | null, mm: number) => zh !== null && mm === min && (zh % 12 === h % 12)
  for (const z of t.matchAll(/halb\s+([a-zäöüß]+|\d+)/g)) if (gleich(stunde(z[1]) !== null ? stunde(z[1])! - 1 : null, 30)) return true
  for (const z of t.matchAll(/viertel\s+nach\s+([a-zäöüß]+|\d+)/g)) if (gleich(stunde(z[1]), 15)) return true
  for (const z of t.matchAll(/viertel\s+vor\s+([a-zäöüß]+|\d+)/g)) if (gleich(stunde(z[1]) !== null ? stunde(z[1])! - 1 : null, 45)) return true
  for (const z of t.matchAll(/dreiviertel\s+([a-zäöüß]+|\d+)/g)) if (gleich(stunde(z[1]) !== null ? stunde(z[1])! - 1 : null, 45)) return true
  for (const z of t.matchAll(/([a-zäöüß]+)\s+uhr/g)) if (gleich(stunde(z[1]), 0)) return true
  return false
}
